import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
	addMonths,
	addQuarters,
	addWeeks,
	addYears,
	endOfDay,
	endOfMonth,
	endOfYear,
	format,
	startOfDay,
	startOfMonth,
	startOfQuarter,
	startOfWeek,
	startOfYear,
	subMonths,
	subQuarters,
	subWeeks,
} from 'date-fns'
import { enUS, ru } from 'date-fns/locale'
import {
	AreaChart, type AreaChartPoint,
	Avatar, AvatarFallback,
	Badge,
	Button,
	type CalendarEvent,
	cn,
	DashboardPanel,
	DashboardStatCard,
	DonutChart, type DonutSegment,
	DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger,
	EventCalendar,
	HorizontalBarChart, type HorizontalBarRow,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	BellIcon,
	ChevronDownIcon,
	EyeIcon,
	HomeIcon,
	MessageCircleIcon,
	TrendingUpIcon,
	XIcon,
} from 'lucide-react'
import {
	useDismissRecommendationMutation,
	useGetClientFlowMetricsQuery,
	useGetFinanceMetricsQuery,
	useGetFinanceSeriesQuery,
	useGetMeQuery,
	useGetPortfolioBreakdownQuery,
	useGetPublicationStatsQuery,
	useListDashboardEventsQuery,
	useListDashboardTasksQuery,
	useListPropertiesQuery,
	useListRecommendationsQuery,
} from '@/api'
import { useUserStore } from '@/lib/store'
import { CREATE_PROPERTY_TYPES } from '@/types'
import type { PropertyType, PublicationMetric, PublicationPeriod } from '@/types'
import { useCan } from '@/lib/access'

export const Route = createFileRoute('/_app/')({
	component: HomePage,
})

const COLORS = {
	rented:      '#5B5BD6',
	available:   '#C7D2FE',
	signing:     '#7C83F0',
	rentPending: '#A5B4FC',
	avito:       '#818CF8',
	cian:        '#6366F1',
	site:        '#A5B4FC',
	accruals:    '#A5B4FC',
	income:      '#6366F1',
	gap:         '#94A3B8',
}

const STAT_CARD_ICONS = {
	tasks:    '/logos/Icon%20+%20Bg3.png',
	requests: '/logos/Icon%20+%20Bg2.png',
	income:   '/logos/Frame%204.png',
	vacancy:  '/logos/Icon%20+%20Bg.png',
}

const statCardIcon = (src: string) => <img src={src} alt="" className="size-10" />

const STAT_CARD_LINK = 'block rounded-(--radius-lg) outline-none focus-visible:ring-3 focus-visible:ring-ring/50 [&>*]:h-full'

const PUBS_METRICS: PublicationMetric[] = ['views', 'requests', 'conversion']
const PUBS_PERIODS: PublicationPeriod[] = [
	'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'allTime',
]

function PanelDropdown<T extends string>({
	value,
	options,
	label,
	onChange,
}: {
	value:    T
	options:  readonly T[]
	label:    (option: T) => string
	onChange: (option: T) => void
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={<Button variant="outline" size="sm" iconRight={<ChevronDownIcon className="size-4 text-(--color-muted-foreground)" />} />}
			>
				{label(value)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-auto min-w-40">
				<DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
					{options.map((o) => (
						<DropdownMenuRadioItem key={o} value={o}>{label(o)}</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

// Calendar bounds in the user's timezone; `to` is exclusive. All time sends
// no bounds and the server counts up to now.
function publicationRange(period: PublicationPeriod, now: Date): { from?: string; to?: string } {
	const range = (from: Date, to: Date) => ({ from: from.toISOString(), to: to.toISOString() })
	const week = startOfWeek(now, { weekStartsOn: 1 })
	switch (period) {
		case 'thisWeek':    return range(week, addWeeks(week, 1))
		case 'lastWeek':    return range(subWeeks(week, 1), week)
		case 'thisMonth':   return range(startOfMonth(now), addMonths(startOfMonth(now), 1))
		case 'lastMonth':   return range(subMonths(startOfMonth(now), 1), startOfMonth(now))
		case 'thisQuarter': return range(startOfQuarter(now), addQuarters(startOfQuarter(now), 1))
		case 'lastQuarter': return range(subQuarters(startOfQuarter(now), 1), startOfQuarter(now))
		case 'thisYear':    return range(startOfYear(now), addYears(startOfYear(now), 1))
		case 'allTime':     return {}
	}
}

const PUBS_ICONS: Record<PublicationMetric, ReactNode> = {
	views:      <EyeIcon />,
	requests:   <MessageCircleIcon />,
	conversion: <TrendingUpIcon />,
}

const PLATFORM_COLORS: Record<string, string> = {
	AVITO: COLORS.avito,
	CIAN:  COLORS.cian,
	SITE:  COLORS.site,
}

// Collapsed, two cards show in full and the third fades out under «Показать все».
const RECOMMENDATIONS_COLLAPSED = 2

function pctChange(raw: string | undefined): { value: string; trend: 'up' | 'down' } | undefined {
	if (!raw) return undefined
	const n = Number(raw)
	if (!Number.isFinite(n) || n === 0) return undefined
	const trend: 'up' | 'down' = n >= 0 ? 'up' : 'down'
	return { value: `${Math.abs(n).toFixed(0)}`, trend }
}

const trendLabel = (c: ReturnType<typeof pctChange>) =>
	c ? `${c.trend === 'up' ? '+' : '-'} ${c.value}%` : undefined

const ISO_DATE = (d: Date) => format(d, 'yyyy-MM-dd')

function HomePage() {
	const { t, i18n } = useTranslation()
	const dateLocale = i18n.language.startsWith('en') ? enUS : ru
	const navigate = useNavigate()
	// Владелец выбирает валюту в профиле, и семь других экранов её уже
	// уважают. Здесь стоял жёсткий рубль, поэтому доход аккаунта на долларах
	// показывался в ₽.
	const currency = useAccountCurrency()
	const money = useMemo(
		() =>
			(value: number) =>
				new Intl.NumberFormat('ru-RU', {
					style: 'currency',
					currency,
					maximumFractionDigits: 0,
				}).format(value),
		[currency],
	)
	const setUser = useUserStore((s) => s.setUser)

	const today = useMemo(() => new Date(), [])

	const [propertyFilter, setPropertyFilter] = useState<PropertyType | 'all'>('all')
	const [pubsMetric, setPubsMetric] = useState<PublicationMetric>('views')
	const [pubsPeriod, setPubsPeriod] = useState<PublicationPeriod>('thisWeek')
	const [financeYear, setFinanceYear] = useState<number>(today.getFullYear())
	const [calendarMonth, setCalendarMonth] = useState<Date>(new Date())
	const [showAllRecommendations, setShowAllRecommendations] = useState(false)

	const financeYears = useMemo(
		() => [0, 1, 2].map((offset) => today.getFullYear() - offset),
		[today],
	)

	const financeBounds = useMemo(() => {
		const from = new Date(financeYear, 0, 1)
		return { from: ISO_DATE(startOfYear(from)), to: ISO_DATE(endOfYear(from)), groupBy: 'month' as const }
	}, [financeYear])

	const calendarBounds = useMemo(
		() => ({
			from: ISO_DATE(startOfMonth(subMonths(calendarMonth, 1))),
			to:   ISO_DATE(endOfMonth(addMonths(calendarMonth, 1))),
		}),
		[calendarMonth],
	)

	const { data: user, isLoading: isUserLoading } = useGetMeQuery()
	// Finances are not every role's: asking anyway answers 403 and fills the
	// access journal.
	const canSeeFinances = useCan('read', 'Invoice')
	const { data: metrics } = useGetFinanceMetricsQuery(undefined, { skip: !canSeeFinances })
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const { data: breakdown } = useGetPortfolioBreakdownQuery(
		propertyFilter === 'all' ? undefined : { type: propertyFilter },
	)
	const pubsRange = useMemo(() => publicationRange(pubsPeriod, today), [pubsPeriod, today])
	const { data: publicationStats } = useGetPublicationStatsQuery({ metric: pubsMetric, ...pubsRange })
	const { data: recommendationsData } = useListRecommendationsQuery({ limit: 10 })
	const [dismissRecommendation] = useDismissRecommendationMutation()

	// Same local-day bounds as /tasks?due=today, so the card matches the list it opens.
	const { data: tasksTodayData } = useListDashboardTasksQuery({
		dueFrom: startOfDay(today).toISOString(),
		dueTo:   endOfDay(today).toISOString(),
		limit:   200,
	})
	const { data: tasksListData } = useListDashboardTasksQuery({ isDone: false, limit: 10 })
	const tasks = tasksListData?.items ?? []
	const { data: financeSeriesData } = useGetFinanceSeriesQuery(financeBounds, { skip: !canSeeFinances })
	const { data: eventsData } = useListDashboardEventsQuery(calendarBounds)
	const { data: clientFlowMetrics } = useGetClientFlowMetricsQuery()

	useEffect(() => {
		if (user) {
			setUser(user)
			if (!user.name) {
				void navigate({ to: '/onboarding/account-type' })
			}
		}
	}, [user, setUser, navigate])

	// ── Donut: my properties by bucket ──────────────────────
	const propertySegments = useMemo<DonutSegment[]>(() => [
		{ id: 'rented',      label: t('dashboard.properties.rented'),          value: breakdown?.rented ?? 0,          color: COLORS.rented },
		{ id: 'available',   label: t('dashboard.properties.available'),       value: breakdown?.available ?? 0,       color: COLORS.available },
		{ id: 'signing',     label: t('dashboard.properties.contractSigning'), value: breakdown?.contractSigning ?? 0, color: COLORS.signing },
		{ id: 'rentPending', label: t('dashboard.properties.rentPending'),     value: breakdown?.rentPending ?? 0,     color: COLORS.rentPending },
	], [breakdown, t])

	const totalProperties = breakdown?.total ?? 0

	// ── Horizontal bars: publications metric by platform ────
	const publicationRows = useMemo<HorizontalBarRow[]>(
		() => (publicationStats?.platforms ?? [])
			.filter((p) => p.platform in PLATFORM_COLORS)
			.map((p) => ({
				id:    p.platform,
				label: t(`dashboard.publications.platforms.${p.platform}`),
				value: p.value,
				color: PLATFORM_COLORS[p.platform]!,
			})),
		[publicationStats, t],
	)

	const formatPublicationValue = (v: number) =>
		pubsMetric === 'conversion' ? `${Math.round(v * 100)}%` : String(Math.round(v))

	// ── KPIs ────────────────────────────────────────────────
	const incomeChange  = pctChange(metrics?.incomeChange)
	const incomeNumber  = Number(metrics?.income ?? 0)
	const debtNumber    = Number(metrics?.debt ?? 0)
	const vacancyLoss   = Number(metrics?.vacancyLoss ?? 0)
	const vacancyChange = pctChange(metrics?.vacancyLossChange)
	const freeProperties = (propertiesData?.properties ?? []).filter((p) => p.status === 'AVAILABLE').length

	const calendarEvents = useMemo<CalendarEvent[]>(() => {
		return (eventsData?.items ?? []).map((ev) => ({
			id:       ev.id,
			date:     new Date(ev.at),
			title:    t(`dashboard.events.kinds.${ev.kind}`),
			subtitle: ev.title,
		}))
	}, [eventsData, t])

	// Series → AreaChartPoint
	const financeSeries = useMemo<AreaChartPoint[]>(() => {
		return (financeSeriesData?.points ?? []).map((p) => {
			const date = new Date(p.bucket)
			const full = format(date, 'LLLL', { locale: dateLocale })
			const capitalized = full.charAt(0).toUpperCase() + full.slice(1)
			return {
				x:        capitalized.slice(0, 3),
				label:    capitalized,
				accruals: Number(p.accruals),
				income:   Number(p.income),
				gap:      Number(p.gap),
			}
		})
	}, [financeSeriesData, dateLocale])

	// KPI «Задачи на сегодня»
	const tasksTodayTotal   = tasksTodayData?.total        ?? 0
	const tasksTodayOverdue = tasksTodayData?.overdueCount ?? 0

	// KPI «Новые обращения»
	const requestsCount = clientFlowMetrics?.requestsCount ?? 0
	const cfChange      = pctChange(clientFlowMetrics?.requestsChange)
	const avgResponseMs = clientFlowMetrics?.avgResponseTimeMs ?? 0
	const avgResponseLabel = avgResponseMs > 0
		? avgResponseMs >= 3_600_000
			? t('dashboard.stats.hoursShort', { count: Math.round(avgResponseMs / 3_600_000) })
			: t('dashboard.stats.minutesShort', { count: Math.max(1, Math.round(avgResponseMs / 60_000)) })
		: undefined

	// ── Recommendations ─────────────────────────────────────
	const recommendations = recommendationsData?.items ?? []
	const recommendationsCollapsed = !showAllRecommendations && recommendations.length > RECOMMENDATIONS_COLLAPSED

	if (isUserLoading) {
		return (
			<div className="flex h-screen items-center justify-center bg-(--color-background)">
				<Spinner size="lg" />
			</div>
		)
	}

	return (
		<>
		<div className="flex flex-col gap-4 p-4">
			{/* Top KPI cards + upcoming events */}
			<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
				<div className="grid gap-3 sm:grid-cols-2">
					<Link to="/tasks" search={{ due: 'today' }} className={STAT_CARD_LINK}>
						<DashboardStatCard
							title={t('dashboard.stats.tasksToday')}
							icon={statCardIcon(STAT_CARD_ICONS.tasks)}
							iconClassName="bg-transparent"
							value={tasksTodayTotal}
							footerValue={t('dashboard.stats.tasksTodayOverdue', { count: tasksTodayOverdue })}
							interactive
						/>
					</Link>

					<Link to="/deals/active" className={STAT_CARD_LINK}>
						<DashboardStatCard
							title={t('dashboard.stats.newRequests')}
							icon={statCardIcon(STAT_CARD_ICONS.requests)}
							iconClassName="bg-transparent"
							value={requestsCount}
							change={trendLabel(cfChange)}
							trend={cfChange?.trend}
							footerValue={avgResponseLabel && t('dashboard.stats.avgResponse', { value: avgResponseLabel })}
							interactive
						/>
					</Link>

					{canSeeFinances && (
						<Link to="/finances/invoices" className={STAT_CARD_LINK}>
							<DashboardStatCard
								title={t('dashboard.stats.incomeMonth')}
								icon={statCardIcon(STAT_CARD_ICONS.income)}
								iconClassName="bg-transparent"
								value={money(incomeNumber)}
								change={trendLabel(incomeChange)}
								trend={incomeChange?.trend}
								footerValue={t('dashboard.stats.incomeMonthDebtLabel', { amount: money(debtNumber) })}
								interactive
							/>
						</Link>
					)}

					{canSeeFinances && (
						<Link to="/properties/registry" search={{ status: 'AVAILABLE' }} className={STAT_CARD_LINK}>
							<DashboardStatCard
								title={t('dashboard.stats.vacancyLoss')}
								icon={statCardIcon(STAT_CARD_ICONS.vacancy)}
								iconClassName="bg-transparent"
								value={money(vacancyLoss)}
								change={trendLabel(vacancyChange)}
								trend={vacancyChange?.trend}
								footerValue={t('dashboard.stats.vacancyLossFree', { count: freeProperties })}
								interactive
							/>
						</Link>
					)}
				</div>

				<DashboardPanel title={t('dashboard.events.title')}>
					<EventCalendar
						events={calendarEvents}
						month={calendarMonth}
						onMonthChange={setCalendarMonth}
						locale={dateLocale}
						todayLabel={t('dashboard.events.today')}
						emptyLabel={t('dashboard.events.empty')}
						onEventClick={(ev) => {
							const raw = eventsData?.items.find((e) => e.id === ev.id)
							const entity = raw?.entity
							if (!entity) return
							const routes: Record<string, string> = {
								DEAL:     `/deals/${entity.id}`,
								RENTAL:   `/rent/${entity.id}`,
								PROPERTY: `/properties/${entity.id}`,
								INVOICE:  `/finances/invoices/${entity.id}`,
								CONTACT:  `/tenants/${entity.id}`,
							}
							const path = routes[entity.type]
							if (path) void navigate({ to: path as never })
						}}
					/>
				</DashboardPanel>
			</div>

			{/* Middle row */}
			<div className="grid gap-3 lg:grid-cols-2">
				<DashboardPanel
					title={t('dashboard.properties.title')}
					actions={
						<PanelDropdown<PropertyType | 'all'>
							value={propertyFilter}
							options={['all', ...CREATE_PROPERTY_TYPES]}
							label={(type) => type === 'all' ? t('dashboard.properties.filterAll') : t(`properties.types.${type}`)}
							onChange={setPropertyFilter}
						/>
					}
				>
					<DonutChart
						segments={propertySegments}
						totalLabel={t('dashboard.properties.total')}
						totalValue={totalProperties}
						size={180}
						thickness={22}
					/>
				</DashboardPanel>

				<DashboardPanel
					title={t('dashboard.publications.title')}
					subtitle={
						<span className="flex items-center gap-3">
							<span className="text-3xl font-[600] leading-9 tracking-[-0.5px] text-(--color-foreground)">
								{formatPublicationValue(publicationStats?.total ?? 0)}
							</span>
							<span className="flex size-10 items-center justify-center rounded-(--radius-lg) bg-(--color-brand-light) text-(--color-brand) [&_svg]:size-5">
								{PUBS_ICONS[pubsMetric]}
							</span>
						</span>
					}
					actions={
						<div className="flex items-center gap-2">
							<PanelDropdown
								value={pubsPeriod}
								options={PUBS_PERIODS}
								label={(p) => t(`dashboard.publications.period.${p}`)}
								onChange={setPubsPeriod}
							/>
							<PanelDropdown
								value={pubsMetric}
								options={PUBS_METRICS}
								label={(m) => t(`dashboard.publications.filter.${m}`)}
								onChange={setPubsMetric}
							/>
						</div>
					}
				>
					<HorizontalBarChart rows={publicationRows} formatValue={formatPublicationValue} />
				</DashboardPanel>
			</div>

			{/* Accrual dynamics + Recommendations */}
			<div className="grid gap-3 lg:grid-cols-2">
				{canSeeFinances && (
					<DashboardPanel
						title={t('dashboard.finance.title')}
						actions={
							<PanelDropdown
								value={String(financeYear)}
								options={financeYears.map(String)}
								label={(y) => y}
								onChange={(y) => setFinanceYear(Number(y))}
							/>
						}
					>
						<AreaChart
							data={financeSeries}
							series={[
								{ id: 'accruals', label: t('dashboard.finance.series.accruals'), color: COLORS.accruals },
								{ id: 'income',   label: t('dashboard.finance.series.income'),   color: COLORS.income },
								{ id: 'gap',      label: t('dashboard.finance.series.gap'),      color: COLORS.gap, tooltipOnly: true },
							]}
							formatValue={(v) => money(v)}
						/>
					</DashboardPanel>
				)}

				<DashboardPanel title={t('dashboard.recommendations.title')}>
					{recommendations.length === 0 ? (
						<p className="text-sm text-(--color-muted-foreground)">{t('dashboard.recommendations.empty')}</p>
					) : (
						<div className="relative">
							<ul
								className={cn(
									'flex flex-col gap-6',
									recommendationsCollapsed && 'max-h-80 overflow-hidden',
								)}
							>
								{recommendations.map((r) => (
									<li key={r.id} className="flex items-start gap-3">
										<span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-(--color-brand-light) text-(--color-brand) [&_svg]:size-5">
											<HomeIcon />
										</span>
										<div className="flex min-w-0 flex-1 flex-col gap-1">
											<span className="text-base text-(--color-foreground)">
												{t('dashboard.recommendations.vacancy.title')}
											</span>
											<p className="text-sm text-(--color-muted-foreground)">
												{t('dashboard.recommendations.vacancy.text', {
													property: r.propertyLabel,
													days: t('dashboard.recommendations.vacancy.days', { count: r.vacantDays }),
												})}
												{r.priceAboveMarketPct != null &&
													t('dashboard.recommendations.vacancy.aboveMarket', { pct: Math.round(r.priceAboveMarketPct) })}
											</p>
											<button
												type="button"
												className="mt-2 w-fit text-sm font-[500] text-(--color-brand) hover:underline"
												onClick={() => void navigate({ to: '/properties/$propertyId', params: { propertyId: r.propertyId } })}
											>
												{t('dashboard.recommendations.vacancy.action')}
											</button>
										</div>
										<button
											type="button"
											aria-label={t('dashboard.recommendations.dismiss')}
											className="rounded-(--radius-sm) p-1 text-(--color-foreground) hover:bg-(--color-muted)"
											onClick={() => void dismissRecommendation(r.id)}
										>
											<XIcon className="size-4" />
										</button>
									</li>
								))}
							</ul>
							{recommendationsCollapsed && (
								<div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-(--color-background) to-transparent" />
							)}
							{recommendations.length > RECOMMENDATIONS_COLLAPSED && (
								<div className={cn('flex justify-center', recommendationsCollapsed ? 'absolute inset-x-0 bottom-0' : 'mt-4')}>
									<Button variant="secondary" size="sm" onClick={() => setShowAllRecommendations((v) => !v)}>
										{t(showAllRecommendations ? 'dashboard.recommendations.collapse' : 'dashboard.recommendations.showAll')}
									</Button>
								</div>
							)}
						</div>
					)}
				</DashboardPanel>
			</div>

			{/* Open tasks */}
			<div className="grid grid-cols-1 gap-3">
				<DashboardPanel
					title={t('dashboard.tasks.title')}
					actions={<Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/tasks' })}>{t('dashboard.tasks.showAll')}</Button>}
				>
					{tasks.length === 0 ? (
						<p className="text-sm text-(--color-muted-foreground)">{t('dashboard.tasks.empty')}</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="hover:bg-transparent">
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.kind')}
									</TableHead>
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.dueAt')}
									</TableHead>
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.entity')}
									</TableHead>
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.responsible')}
									</TableHead>
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.description')}
									</TableHead>
									<TableHead className="px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.priority')}
									</TableHead>
									<TableHead className="w-[120px] px-2 font-[500] text-(--color-muted-foreground)">
										{t('dashboard.tasks.columns.reminder')}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{tasks.map((task) => {
									const due = new Date(task.dueAt)
									const dueStr = due.toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: 'short' })
									const dueTimeStr = due.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
									return (
										<TableRow key={task.id} className="h-14 cursor-pointer" onClick={() => void navigate({ to: '/tasks', search: { taskId: task.id } })}>
											<TableCell className="px-2 text-sm">
												{t(`dashboard.tasks.kinds.${task.kind}`)}
											</TableCell>
											<TableCell
												className={
													task.isOverdue
														? 'px-2 text-sm font-[500] text-(--color-destructive)'
														: 'px-2 text-sm text-(--color-foreground)'
												}
											>
												{dueStr}, {dueTimeStr}
											</TableCell>
											<TableCell className="px-2">
												<span className="text-sm text-(--color-info)">{task.entity?.label ?? '—'}</span>
											</TableCell>
											<TableCell className="px-2">
												{task.responsibleName ? (
													<div className="flex items-center gap-2">
														<Avatar className="size-6">
															<AvatarFallback className="text-[10px]">
																{task.responsibleName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
															</AvatarFallback>
														</Avatar>
														<span className="text-sm">{task.responsibleName}</span>
													</div>
												) : (
													<span className="text-sm text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-2 text-sm text-(--color-foreground)">
												{task.title}
											</TableCell>
											<TableCell className="px-2">
												<Badge
													size="sm"
													variant={
														task.priority === 'CRITICAL' ? 'destructive'
														: task.priority === 'ATTENTION' ? 'warning'
														: 'secondary'
													}
												>
													{t(`dashboard.tasks.priorities.${task.priority}`)}
												</Badge>
											</TableCell>
											<TableCell className="w-[120px] px-2">
												{task.reminderAt && (
													<span className="inline-flex items-center gap-1 text-xs text-(--color-muted-foreground)">
														<BellIcon className="size-3" />
														{t('dashboard.tasks.reminderIn', { count: 1 })}
													</span>
												)}
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					)}
				</DashboardPanel>
			</div>
		</div>

		</>
	)
}
